import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Save, Trash2, Check, LayoutTemplate, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";

const MODULES = [
  { key: "welcome", label: "Welcome & Goodbye" },
  { key: "autoroles", label: "Auto-Roles" },
];

export default function Templates() {
  const { guildId, guild } = useGuild();
  const [templates, setTemplates] = useState([]);
  const [servers, setServers] = useState([]);
  const [name, setName] = useState("");
  const [mods, setMods] = useState({ welcome: true, autoroles: true });
  const [copyTo, setCopyTo] = useState("");
  const [applyTo, setApplyTo] = useState({});

  const load = () => api.get("/templates").then((r) => setTemplates(r.data));
  useEffect(() => {
    load();
    api.get("/guilds").then((r) => setServers(r.data));
  }, [guildId]);

  const selectedMods = () => Object.keys(mods).filter((k) => mods[k]);
  const others = servers.filter((s) => s.id !== guildId);

  const saveTemplate = async () => {
    if (!name.trim()) { toast.error("Name your template"); return; }
    if (selectedMods().length === 0) { toast.error("Pick at least one module"); return; }
    await api.post("/templates", { name: name.trim(), guild_id: guildId, modules: selectedMods() });
    setName("");
    toast.success("Template saved");
    load();
  };

  const apply = async (tid) => {
    const target = applyTo[tid];
    if (!target) { toast.error("Choose a server to apply to"); return; }
    const res = await api.post(`/templates/${tid}/apply`, { guild_id: target });
    toast.success(`Applied to server (${res.data.applied.join(", ")})`);
  };

  const quickCopy = async () => {
    if (!copyTo) { toast.error("Choose a destination server"); return; }
    if (selectedMods().length === 0) { toast.error("Pick at least one module"); return; }
    const res = await api.post("/templates/copy", { from_guild_id: guildId, to_guild_id: copyTo, modules: selectedMods() });
    toast.success(`Copied to server (${res.data.applied.join(", ")})`);
  };

  const del = async (tid) => { await api.delete(`/templates/${tid}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <PageHead title="Config Templates" desc="Save this server's setup as a preset and apply it to your other servers in one click." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Save / quick copy */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
          <h3 className="font-head font-semibold flex items-center gap-2"><Save className="w-4 h-4 text-volt" /> Save from <span className="text-volt">{guild?.name || "this server"}</span></h3>
          <div className="flex flex-wrap gap-3">
            {MODULES.map((m) => (
              <button key={m.key} data-testid={`mod-toggle-${m.key}`} onClick={() => setMods((p) => ({ ...p, [m.key]: !p[m.key] }))}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm border transition-colors ${mods[m.key] ? "bg-volt border-volt text-volt-ink font-semibold" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                {mods[m.key] && <Check className="w-3.5 h-3.5" />} {m.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="template-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My standard onboarding"
              className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm" />
            <button data-testid="template-save" onClick={saveTemplate} className="bg-volt text-volt-ink font-semibold rounded-md px-4 flex items-center gap-2 text-sm"><Save className="w-4 h-4" /> Save preset</button>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <h4 className="text-sm font-semibold flex items-center gap-2 mb-3"><Copy className="w-4 h-4 text-volt" /> Quick copy to another server</h4>
            {others.length === 0 ? <p className="text-sm text-zinc-600">You only manage this one server.</p> : (
              <div className="flex gap-2">
                <select data-testid="copy-target" value={copyTo} onChange={(e) => setCopyTo(e.target.value)} className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm">
                  <option value="">Choose destination…</option>
                  {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <button data-testid="copy-go" onClick={quickCopy} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-4 flex items-center gap-2 text-sm"><ArrowRight className="w-4 h-4" /> Copy</button>
              </div>
            )}
            <p className="text-xs text-zinc-600 mt-2">Copies the selected modules' settings straight into the destination server.</p>
          </div>
        </div>

        {/* Saved templates */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold flex items-center gap-2 mb-4"><LayoutTemplate className="w-4 h-4 text-volt" /> My templates</h3>
          {templates.length === 0 ? <p className="text-sm text-zinc-600">No saved templates yet. Save one on the left to reuse it across your servers.</p> : (
            <div className="space-y-3">
              {templates.map((t) => (
                <div key={t.id} data-testid={`template-${t.id}`} className="bg-black border border-zinc-800 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">From {t.source_guild_name || "a server"} · {t.modules.join(", ")}</div>
                    </div>
                    <button data-testid={`template-del-${t.id}`} onClick={() => del(t.id)} className="text-zinc-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <select data-testid={`apply-target-${t.id}`} value={applyTo[t.id] || ""} onChange={(e) => setApplyTo((p) => ({ ...p, [t.id]: e.target.value }))}
                      className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm">
                      <option value="">Apply to…</option>
                      {servers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button data-testid={`apply-go-${t.id}`} onClick={() => apply(t.id)} className="bg-volt text-volt-ink font-semibold rounded-md px-4 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Apply</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
